import React, { useCallback, useEffect, useRef, useState } from "react";

import { Divider } from "antd";
import { TextAreaProps } from "antd/es/input/TextArea";
import cn from "classnames";
import { BaseEditor, Descendant, Editor, Transforms } from "slate";
import { HistoryEditor } from "slate-history";
import type { ReactEditor } from "slate-react";
import { Editable, Slate } from "slate-react";
import { EditableProps } from "slate-react/dist/components/editable";

import EmailDynamicVariable from "@mapmycustomers/shared/types/email/EmailDynamicVariable";
import { EntityTypesSupportedByEmailFeature } from "@mapmycustomers/shared/types/entity";
import Resource from "@mapmycustomers/shared/types/entity/Resource";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import { useConfigProvider } from "../../ConfigProvider";
import LoadingSpinner from "../../LoadingSpinner";
import resetEditor from "../../utils/resetEditor";
import AttachedFileList from "../EmailAttachedFileList";
import EmailAttachedFile from "../EmailAttachedFileList/EmailAttachedFile";

import Actions from "./component/Actions";
import EditorElement from "./component/EditorElement";
import Leaf from "./component/Leaf";
import Toolbar from "./component/Toolbar";
import styles from "./EmailBody.module.scss";
import ActionCallback from "./type/ActionCallback";
import AttachedFileCallback from "./type/AttachedFileCallback";
import EmailBodyEditor from "./type/EmailBodyEditor";
import FileAttachCallback from "./type/FileAttachCallback";
import ImageUploadCallback from "./type/ImageUploadCallback";
import ResourceCallback from "./type/ResourceCallback";
import ResourceGetter from "./type/ResourceGetter";
import ResourceLinkInsertCallback from "./type/ResourceLinkInsertCallback";
import ResourceMetadataGetter from "./type/ResourceMetadataGetter";
import createEmailBodyEditor from "./util/createEmailBodyEditor";
import decorator from "./util/decorator";
import deserializeFromHTML from "./util/deserializeFromHTML";
import serializeToHTML from "./util/serializeToHTML";
import useResourceLinks from "./util/useResourceLinks";
import useShortcuts from "./util/useShortcuts";

type EditorType = ReactEditor & BaseEditor & HistoryEditor;

export interface EmailBodyProps extends Pick<
  TextAreaProps,
  "autoFocus" | "className" | "disabled" | "maxLength" | "placeholder" | "rows"
> {
  action?: React.ReactNode;
  aiWriterVisible?: boolean;
  attachedFiles?: EmailAttachedFile[];
  dynamicVariables?: EmailDynamicVariable[];
  editableClassName?: string;
  editableRenderer?: (editable: React.ReactNode) => React.ReactNode;
  editorContentClassName?: string;
  editorContentWrapperClassName?: string;
  editorId?: string;
  entityType?: EntityTypesSupportedByEmailFeature;
  footer?: React.ReactNode;
  loading?: boolean;
  maxLength?: number;
  onAction?: ActionCallback;
  onAiWriterToggle?: () => void;
  onChange?: (value: string) => void;
  onFileAttach?: FileAttachCallback;
  onFileDelete?: AttachedFileCallback;
  onFileDownload?: AttachedFileCallback;
  onFilePreview?: AttachedFileCallback;
  onImageUpload?: ImageUploadCallback;
  onInit?: (editor: EmailBodyEditor) => void;
  onPreview?: () => void;
  onResourceDownload?: ResourceCallback;
  onResourceLinkInsert?: ResourceLinkInsertCallback;
  onResourceLinksChange?: (resourceIds: Resource["id"][]) => void;
  onUnsubscribeLinkIncludedChange?: (value: boolean) => void;
  placeholder?: string;
  previewDisableText?: string;
  readOnly?: boolean;
  recipientCount?: number;
  reply?: boolean;
  resourceGetter?: ResourceGetter;
  resourceMetadataGetter?: ResourceMetadataGetter;
  showActions?: boolean;
  unsubscribeLinkIncluded?: boolean;
  value?: string;
}

const EmailBody = ({
  action,
  aiWriterVisible,
  attachedFiles,
  className,
  dynamicVariables,
  editableClassName,
  editableRenderer,
  editorContentClassName,
  editorContentWrapperClassName,
  editorId,
  entityType,
  footer,
  loading,
  onAction,
  onAiWriterToggle,
  onChange,
  onFileAttach,
  onFileDelete,
  onFileDownload,
  onFilePreview,
  onImageUpload,
  onInit,
  onPreview,
  onResourceDownload,
  onResourceLinkInsert,
  onResourceLinksChange,
  onUnsubscribeLinkIncludedChange,
  placeholder,
  previewDisableText,
  readOnly,
  recipientCount,
  reply,
  resourceGetter,
  resourceMetadataGetter,
  showActions,
  unsubscribeLinkIncluded,
  value = "",
  ...props
}: EmailBodyProps) => {
  const configProvider = useConfigProvider();

  const editorRef = useRef<EditorType>(createEmailBodyEditor(onImageUpload));

  const editor = editorRef.current!;

  const [initialDescendants] = useState(() => {
    editor.children = deserializeFromHTML(value);
    // <Slate initialValue> is the one path Slate does not normalize, so normalize the editor once
    // to give an inline void (image) its surrounding text nodes; edits normalize automatically.
    HistoryEditor.withoutSaving(editor, () => Editor.normalize(editor, { force: true }));
    return editor.children;
  });

  const descendantsRef = useRef<Descendant[]>(initialDescendants);

  const valueRef = useRef<string>(value);

  useShortcuts({ editor, onImageUpload, readOnly });

  const { getResourceIds, insertResourceLink, removeResourceLinks } = useResourceLinks(editor);

  const renderLeaf = useCallback<NonNullable<EditableProps["renderLeaf"]>>(
    (props) => <Leaf {...props} />,
    []
  );

  const serializeDescendantsToHTml = useDynamicCallback((descendants: Descendant[]) => {
    return serializeToHTML({ configProvider, descendants, resourceGetter });
  });

  const getValue = useDynamicCallback(() => {
    return serializeDescendantsToHTml(editor.children);
  });

  const setValue = useDynamicCallback((value: string) => {
    resetEditor(editor);

    const nodes = deserializeFromHTML(value);

    // insertNodes normalizes as it inserts, so an inline void (image) gets its surrounding text nodes
    Transforms.insertNodes(editor, nodes);

    descendantsRef.current = editor.children;
    valueRef.current = value;
  });

  const handleChange = useDynamicCallback((descendants: Descendant[]) => {
    const value = serializeDescendantsToHTml(descendants);

    if (descendantsRef.current !== descendants) {
      onChange?.(value);
    }

    descendantsRef.current = descendants;
    valueRef.current = value;
  });

  const handleResourceLinkRemoved = useCallback(() => {
    if (!onResourceLinksChange) {
      return;
    }

    const resourceIds = getResourceIds();
    onResourceLinksChange(Array.from(resourceIds));
  }, [getResourceIds, onResourceLinksChange]);

  useEffect(() => {
    if (onInit) {
      onInit({
        getResourceIds,
        getValue,
        insertResourceLink,
        removeResourceLinks,
        setValue,
      });
    }
  }, [onInit, insertResourceLink, removeResourceLinks, getResourceIds, getValue, setValue]);

  useEffect(() => {
    if (valueRef.current !== value) {
      setValue(value);
    }
  }, [editor, value, setValue]);

  const renderElement = useDynamicCallback<NonNullable<EditableProps["renderElement"]>>(
    ({ attributes, children, element }) => (
      <EditorElement
        attributes={attributes}
        dynamicVariables={dynamicVariables}
        element={element}
        entityType={entityType}
        onResourceDownload={onResourceDownload}
        onResourceLinkRemoved={handleResourceLinkRemoved}
        readOnly={readOnly}
        resourceGetter={resourceGetter}
        resourceMetadataGetter={resourceMetadataGetter}
      >
        {children}
      </EditorElement>
    )
  );

  const editable = (
    <Editable
      className={cn(styles.editable, editableClassName, {
        // TODO: should there be a better way to check for emptiness?
        "mmc-js-empty": !value?.trim() || value.trim() === "<div ><br/></div>",
      })}
      decorate={(nodeEntry) => decorator(editor, nodeEntry)}
      id={editorId}
      placeholder={placeholder}
      readOnly={readOnly}
      renderElement={renderElement}
      renderLeaf={renderLeaf}
      renderPlaceholder={() => (
        <span>{configProvider.formatMessage("ui.emailBody.placeholder")}</span>
      )}
      {...props}
    />
  );

  return (
    <div className={cn(styles.container, { [styles.showActions]: showActions }, className)}>
      <Slate
        editor={editor}
        initialValue={initialDescendants}
        onChange={props.disabled ? undefined : handleChange}
      >
        <div className={styles.editorContainer}>
          <div
            className={cn(
              editorContentWrapperClassName,
              {
                [styles.bordered]: !readOnly && !showActions,
              },
              styles.editorContentWrapper
            )}
          >
            <div className={cn(editorContentClassName, styles.editorContent)}>
              <AttachedFileList
                files={attachedFiles}
                onFileDelete={onFileDelete}
                onFileDownload={onFileDownload}
                onFilePreview={onFilePreview}
                readOnly={readOnly}
              />
              {!!attachedFiles?.length && <Divider className={styles.divider} />}
              {editableRenderer ? editableRenderer(editable) : editable}
            </div>
          </div>
          {footer}
          {!readOnly && onImageUpload && (
            <div className={styles.actionsAndToolbarWrapper}>
              {!showActions && <Toolbar extraMargin onImageUpload={onImageUpload} showInsertLink />}
              {showActions && (
                <Actions
                  action={action}
                  aiWriterVisible={aiWriterVisible}
                  dynamicVariables={dynamicVariables}
                  entityType={entityType}
                  onAction={onAction}
                  onAiWriterToggle={onAiWriterToggle}
                  onFileAttach={onFileAttach}
                  onImageUpload={onImageUpload}
                  onPreview={onPreview}
                  onResourceLinkInsert={onResourceLinkInsert}
                  onUnsubscribeLinkIncludedChange={onUnsubscribeLinkIncludedChange}
                  previewDisableText={previewDisableText}
                  recipientCount={recipientCount}
                  reply={reply}
                  unsubscribeLinkIncluded={unsubscribeLinkIncluded}
                />
              )}
            </div>
          )}
        </div>
      </Slate>
      {loading && (
        <div className={styles.loading}>
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
};

export default EmailBody;
